import { useCallback, useEffect, useRef, useState } from "react";
import { fetchWithAuth } from "../utils/api";
import { useSocket } from "../context/SocketContext";
import usePoll from "./usePoll";

/**
 * Real feed for the header inbox dropdown — the current user's rows from the
 * `notification` table (order-note @mentions, CS ticket assignments; any
 * future system event that INSERTs a notification shows up here
 * automatically).
 *
 * Live-updates via the `notifications:updated` socket event (emitted to the
 * user's room whenever their feed changes), with a once-a-minute poll plus
 * tab focus/visibility refresh as fallback so the unread badge stays honest
 * even if the socket is down.
 */

const POLL_MS = 60_000;

export default function useInboxFeed() {
  const [notifications, setNotifications] = useState([]);
  const inFlight = useRef(false);
  const socket = useSocket()?.socket;

  const refresh = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await fetchWithAuth("/admin/notifications");
      if (res.ok) {
        const data = await res.json();
        setNotifications(Array.isArray(data.notifications) ? data.notifications : []);
      } else {
        return false; // usePoll backs off while the API struggles
      }
    } catch {
      // transient failure — keep the last known feed
    } finally {
      inFlight.current = false;
    }
  }, []);

  // Instant updates — the backend emits to this user's room on any feed
  // change (new notification, CS ticket resolved/reopened/reassigned).
  useEffect(() => {
    if (!socket) return undefined;
    const handler = () => refresh();
    socket.on("notifications:updated", handler);
    return () => socket.off("notifications:updated", handler);
  }, [socket, refresh]);

  const pollNow = usePoll(refresh, POLL_MS, { refreshOnVisible: true });
  // Window focus also refetches (a click back into an already-visible window
  // doesn't fire visibilitychange) — through the poll's own trigger, so it
  // can never overlap a tick.
  useEffect(() => {
    window.addEventListener("focus", pollNow);
    return () => window.removeEventListener("focus", pollNow);
  }, [pollNow]);

  const markRead = useCallback(async (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    try {
      await fetchWithAuth(`/admin/notifications/${id}/read`, { method: "POST" });
    } catch {
      // best effort — the next poll resyncs
    }
  }, []);

  const markUnread = useCallback(async (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: false } : n))
    );
    try {
      await fetchWithAuth(`/admin/notifications/${id}/unread`, { method: "POST" });
    } catch {
      // best effort — the next poll resyncs
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await fetchWithAuth("/admin/notifications/read-all", { method: "POST" });
    } catch {
      // best effort — the next poll resyncs
    }
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return { notifications, unreadCount, markRead, markUnread, markAllRead, refresh };
}
